import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { QRCodeSVG } from 'qrcode.react';

const DeliveryTimeline = ({ package: pkg }) => {
  const getStatusConfig = (status) => {
    const configs = {
      'Production terminée': { icon: 'CheckCircle', color: 'text-success' },
      'Emballage': { icon: 'Package', color: 'text-primary' },
      'En transit': { icon: 'Truck', color: 'text-primary' },
      'Centre de distribution': { icon: 'Building', color: 'text-warning' },
      'En cours de livraison': { icon: 'MapPin', color: 'text-warning' },
      'Livré': { icon: 'CheckCircle', color: 'text-success' }
    };
    return configs?.[status] || { icon: 'Circle', color: 'text-gray-400' };
  };

  const formatDateTime = (timestamp) => {
    const date = new Date(timestamp);
    return {
      date: date?.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }),
      time: date?.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
    };
  };

  const downloadQRCode = () => {
    const svg = document.getElementById('package-qr-code');
    const svgData = new XMLSerializer()?.serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas?.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img?.width;
      canvas.height = img?.height;
      ctx?.drawImage(img, 0, 0);
      const pngFile = canvas?.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.download = `QR-${pkg?.packageNumber}.png`;
      downloadLink.href = pngFile;
      downloadLink?.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      {/* Package Header */}
      <div className="flex items-start justify-between mb-6 pb-6 border-b border-gray-200">
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{pkg?.packageNumber}</h2>
          <div className="space-y-1 text-sm">
            <div className="flex items-center gap-2">
              <Icon name="User" size={16} className="text-gray-400" />
              <span className="text-gray-600">Patient:</span>
              <span className="font-medium text-gray-900">{pkg?.patientName}</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="Building" size={16} className="text-gray-400" />
              <span className="text-gray-600">Clinique:</span>
              <span className="font-medium text-gray-900">{pkg?.clinicName}</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="Truck" size={16} className="text-gray-400" />
              <span className="text-gray-600">Transporteur:</span>
              <span className="font-medium text-gray-900">{pkg?.carrier}</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="Hash" size={16} className="text-gray-400" />
              <span className="text-gray-600">Numéro de suivi:</span>
              <span className="font-mono text-sm font-medium text-gray-900">{pkg?.trackingNumber}</span>
            </div>
          </div>
        </div>

        {/* QR Code */}
        <div className="bg-white p-4 rounded-lg border-2 border-gray-300">
          <QRCodeSVG
            id="package-qr-code"
            value={pkg?.qrCode}
            size={120}
            level="H"
            includeMargin={true}
          />
          <Button
            variant="outline"
            size="sm"
            iconName="Download"
            onClick={downloadQRCode}
            className="w-full mt-2"
          >
            Télécharger QR
          </Button>
        </div>
      </div>

      {/* Delivery Status */}
      <div className="mb-6">
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-600 mb-1">Statut actuel</div>
              <div className="text-lg font-semibold text-gray-900">{pkg?.currentLocation}</div>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-600 mb-1">Livraison estimée</div>
              <div className="text-lg font-semibold text-primary">
                {formatDateTime(pkg?.estimatedDelivery)?.date}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Historique de livraison</h3>
        <div className="space-y-4">
          {pkg?.deliveryHistory?.map((event, index) => {
            const config = getStatusConfig(event?.status);
            const dateTime = formatDateTime(event?.timestamp);
            const isLast = index === pkg?.deliveryHistory?.length - 1;

            return (
              <div key={index} className="flex gap-4">
                {/* Timeline Line */}
                <div className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    index === 0 ? 'bg-primary' : 'bg-gray-200'
                  }`}>
                    <Icon
                      name={config?.icon}
                      size={20}
                      className={index === 0 ? 'text-white' : config?.color}
                    />
                  </div>
                  {!isLast && (
                    <div className="w-0.5 h-full min-h-[40px] bg-gray-200 mt-2"></div>
                  )}
                </div>

                {/* Event Details */}
                <div className="flex-1 pb-4">
                  <div className="flex items-start justify-between mb-1">
                    <h4 className="font-semibold text-gray-900">{event?.status}</h4>
                    <div className="text-right">
                      <div className="text-sm font-medium text-gray-900">{dateTime?.date}</div>
                      <div className="text-xs text-gray-600">{dateTime?.time}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Icon name="MapPin" size={14} />
                    <span>{event?.location}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Actions */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="flex gap-3">
          <Button variant="outline" iconName="Download" className="flex-1">
            Télécharger reçu
          </Button>
          <Button variant="outline" iconName="FileText" className="flex-1">
            Certificat qualité
          </Button>
          <Button variant="primary" iconName="MessageSquare" className="flex-1">
            Contacter support
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DeliveryTimeline;